import React, { useState, useEffect, useRef } from "react";
import { Button, Input } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import cloneDeep from "lodash/cloneDeep";
import { toast } from "react-toastify";
import { getSpecsUnits } from "../../../../actions/specManager";
import {
  getUnitOptions,
  computeTestSpecPreviewAdvanceState,
  globalTestSpecSpecFormBlocksSave,
  isCustomSpecProduct,
} from "../utils/utils";
import microscopeIcon from "../../../../assets/images/product/specmanager/microscope-line.svg";
import arrowRightLineIcon from "../../../../assets/images/product/specmanager/arrow-right-line.svg";
import notConfiguredIcon from "../../../../assets/images/product/specmanager/error-warning-fill.svg";
import arrowRightIcon from "../../../../assets/images/product/specmanager/arrow-right-s-line.svg";
import pendingStatusIcon from "../../../../assets/images/product/specmanager/circle-line.svg";
import checkboxCircleFillIcon from "../../../../assets/images/product/specmanager/checkbox-circle-fill.svg";
import { CoverageSummarySection } from "../shared/specManagerUi";
import TestSpecForm from "./TestSpecForm";
import UpdateProductListPanel from "./UpdateProductListPanel";
import TestSpecPreviewStep from "./TestSpecPreviewStep";
import "./TestSpecEdit.css";

export default function TestSpecEditor({
  customer,
  identifierFields,
  testName,
  specRows,
  selectedProducts,
  customSnapshotsRef,
  isPreviewStep,
  hasDraftChanges,
  productSearchValue,
  onProductSearchChange,
  onSpecRowsChange,
  onSelectedProductsChange,
  onPreviewStepChange,
  onSaveClick,
  onBack,
  saving,
}) {
  const [unitOptions, setUnitOptions] = useState([]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    let cancelled = false;
    getSpecsUnits(abortController.signal, customer).then((response) => {
      if (cancelled) return;
      if (!response.success) {
        toast.error(response.message);
        return;
      }
      setUnitOptions(["None", ...response.result]);
    });
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [customer]);

  const previewState = computeTestSpecPreviewAdvanceState({
    loading: false,
    dataOfSpecEditing: specRows,
    testSpecEditHasDraftChanges: hasDraftChanges,
    selectedProductCount: selectedProducts.length,
    testSpecAddCountFromAllList: 0,
    testSpecSelectedCount: selectedProducts.length,
    testSpecAllProductsLoading: false,
    testSpecHasStep1Baseline: true,
  });

  const specRow = specRows[0];
  const standardProductCount = selectedProducts.filter(
    (productRow) => !isCustomSpecProduct(productRow),
  ).length;
  const hasCustomProducts = selectedProducts.some(isCustomSpecProduct);
  const hasMrlCountries = Array.isArray(specRow.countries_lst) && specRow.countries_lst.length > 0;
  const hasNoStandardSpec = !specRow.test_type && !hasMrlCountries;
  const customOnlyNoStandard = hasNoStandardSpec
    && selectedProducts.length > 0
    && selectedProducts.every(isCustomSpecProduct);

  const handleHeaderBack = isPreviewStep ? () => onPreviewStepChange(false) : onBack;

  return (
    <div className="TestSpecEdit_Root">
      <div className="TestSpecEdit_Header">
        <div className="TestSpecEdit_HeaderLeft">
          <button
            type="button"
            className="SpecManager_EditorBackButton"
            onClick={handleHeaderBack}
            aria-label="Go back"
          >
            <ArrowLeftOutlined />
          </button>
          <span className="TestSpecEdit_HeaderTitle" title={testName}>{testName}</span>
        </div>
        <div className="TestSpecEdit_HeaderRight">
          <div className="TestSpecEdit_SearchBox">
            <Input
              className="TestSpecEdit_SearchInput"
              placeholder="Search products..."
              value={productSearchValue}
              onChange={(event) => onProductSearchChange(event.target.value)}
              allowClear
            />
          </div>
          <div className={`TestSpecEdit_Steps${isPreviewStep ? " TestSpecEdit_Steps--step2" : " TestSpecEdit_Steps--step1"}`}>
            <div className={`TestSpecEdit_Step${!isPreviewStep ? " TestSpecEdit_Step--active" : " TestSpecEdit_Step--completed"}`}>
              <img
                src={!isPreviewStep ? pendingStatusIcon : checkboxCircleFillIcon}
                alt=""
                className="TestSpecEdit_StepIcon"
                aria-hidden
              />
              <span>Select Products</span>
            </div>
            <img src={arrowRightIcon} alt="" className="TestSpecEdit_StepArrow" aria-hidden />
            <div className={`TestSpecEdit_Step${isPreviewStep ? " TestSpecEdit_Step--active" : " TestSpecEdit_Step--inactive"}`}>
              <img src={pendingStatusIcon} alt="" className="TestSpecEdit_StepIcon" aria-hidden />
              <span>Preview Selection</span>
            </div>
          </div>
        </div>
      </div>

      <div className="TestSpecEdit_Body">
        {isPreviewStep ? (
          <TestSpecPreviewStep
            testName={testName}
            specRow={specRow}
            selectedProducts={selectedProducts}
            identifierFields={identifierFields}
            searchValue={productSearchValue}
          />
        ) : (
          <>
            <div className="TestSpecEdit_LeftPanel">
              <TestSpecForm
                specRows={specRows}
                unitOptions={getUnitOptions(unitOptions)}
                customOnlyNoStandard={customOnlyNoStandard}
                onSpecRowsChange={(updatedRows) => onSpecRowsChange(cloneDeep(updatedRows))}
              />
              <CoverageSummarySection products={selectedProducts} />
              {hasCustomProducts && (
                <div className="TestSpecEdit_CustomAlert" role="status">
                  <img src={notConfiguredIcon} alt="" className="TestSpecEdit_CustomAlertIcon" aria-hidden />
                  <div>
                    <div className="TestSpecEdit_CustomAlertTitle">
                      Changes apply to all products using this standard.
                    </div>
                    <div className="TestSpecEdit_CustomAlertText">
                      Products with custom limits will keep their individual values.
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="TestSpecEdit_RightPanel">
              <UpdateProductListPanel
                customer={customer}
                identifierFields={identifierFields}
                selectedProducts={selectedProducts}
                customSnapshotsRef={customSnapshotsRef}
                searchValue={productSearchValue}
                onSelectedProductsChange={onSelectedProductsChange}
              />
            </div>
          </>
        )}
      </div>

      <div className="TestSpecEdit_Footer">
        <div className="TestSpecEdit_FooterBadge">
          <img src={microscopeIcon} alt="" className="TestSpecEdit_FooterBadgeIcon" aria-hidden />
          <span>
            <strong>
              {standardProductCount}
              {" "}
              Products
            </strong>
            {" "}
            follow the standard spec
          </span>
        </div>
        <div className="TestSpecEdit_FooterActions">
          {isPreviewStep ? (
            <>
              <Button onClick={() => onPreviewStepChange(false)}>Back</Button>
              <Button
                type="primary"
                loading={saving}
                disabled={globalTestSpecSpecFormBlocksSave(specRows) || !hasDraftChanges}
                onClick={onSaveClick}
              >
                Save Spec
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onBack}>Cancel</Button>
              <Button
                type="primary"
                disabled={previewState.previewPrimaryDisabled}
                title={previewState.previewSelectionTooltip}
                onClick={() => onPreviewStepChange(true)}
              >
                <span className="TestSpecEdit_FooterPrimaryLabel">
                  Preview Selection
                  <img src={arrowRightLineIcon} alt="" className="TestSpecEdit_FooterPrimaryArrow" aria-hidden />
                </span>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
